import { useEffect, useId, useState } from 'react';
import type { ForwardInfo, ForwardMode } from '../api';
import { forwardHelp, forwardStatus } from '../phone/forward-text';
import { formatPhone } from '../phone/number';

/** Settings: "Forward calls" — off / when I don't answer / always, and the number. */
export function ForwardSetting({
  forward,
  disabled,
  onSave,
}: {
  forward: ForwardInfo | null;
  disabled: boolean;
  onSave: (change: { mode: ForwardMode; number?: string | null }) => Promise<string | null>;
}) {
  const id = useId();
  const [mode, setMode] = useState<ForwardMode>(forward?.mode ?? 'off');
  const [number, setNumber] = useState(forward?.number ? formatPhone(forward.number) : '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  // another device changed it: follow (unless the person is editing here)
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (dirty || saving) return;
    setMode(forward?.mode ?? 'off');
    setNumber(forward?.number ? formatPhone(forward.number) : '');
  }, [forward?.mode, forward?.number]); // eslint-disable-line react-hooks/exhaustive-deps

  const savedMode = forward?.mode ?? 'off';
  const savedNumber = forward?.number ?? '';
  const typedDigits = number.replace(/\D/g, '');
  const changed = mode !== savedMode || (mode !== 'off' && typedDigits.slice(-10) !== savedNumber.slice(-10));

  const save = async (nextMode = mode) => {
    setSaving(true);
    setMessage(null);
    // off keeps the saved number (so turning it on again is one click)
    const err = await onSave(nextMode === 'off' ? { mode: 'off' } : { mode: nextMode, number });
    setSaving(false);
    if (err) setMessage({ tone: 'bad', text: err });
    else {
      setDirty(false);
      // on: the status line below says what happens now ("Setting up…" / "On: …")
      setMessage(nextMode === 'off' ? { tone: 'ok', text: 'Forwarding is off.' } : null);
    }
  };

  const status = forwardStatus(forward);
  const busy = disabled || saving;

  return (
    <div className="setting setting-stack">
      <div className="setting-text">
        <span className="setting-name" id={`${id}-label`}>
          Forward calls
        </span>
        <span className="setting-help" id={`${id}-help`}>
          {forwardHelp(mode)}
        </span>
      </div>

      <div className="segmented" role="radiogroup" aria-labelledby={`${id}-label`} aria-describedby={`${id}-help`}>
        {(
          [
            ['off', 'Off'],
            ['noanswer', 'If I don’t answer'],
            ['always', 'Always'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            className="segment"
            disabled={busy}
            onClick={() => {
              setMode(value);
              setDirty(true);
              setMessage(null);
              // switching off needs no number: save at once
              if (value === 'off' && savedMode !== 'off') void save('off');
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {mode !== 'off' && (
        <form
          className="forward-number"
          onSubmit={(e) => {
            e.preventDefault();
            if (changed) void save();
          }}
        >
          <label className="field">
            <span>Forward to</span>
            <input
              name="forward-number"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+1 (209) 555-0123"
              value={number}
              disabled={busy}
              onChange={(e) => {
                setNumber(e.target.value);
                setDirty(true);
                setMessage(null);
              }}
            />
          </label>
          <button className="btn btn-small" type="submit" disabled={busy || !changed || !typedDigits}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      )}

      {!dirty && status && !message && (
        <p className={`setting-msg setting-msg-${status.tone}`} role="status">
          {status.text}
        </p>
      )}
      {message && (
        <p className={`setting-msg setting-msg-${message.tone}`} role={message.tone === 'bad' ? 'alert' : 'status'}>
          {message.text}
        </p>
      )}
    </div>
  );
}
